import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from "@sentry/react";
import { Button, Image, Text, useTheme, View } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { Dimensions, Linking, SafeAreaView, ScrollView, StyleSheet } from "react-native";
import { Icon } from "react-native-elements";
import { useSelector } from "react-redux";

import MenstrualCycleDiagram from "../../components/MenstrualCycleDiagram";
import { Routes, Scale, VerticalScale } from "../../constants";
import { HORMONE_HEX_COLOR } from "../../constants/theme";
import {
  dateOfNextMenstruation,
  getCurrentMenstrualCycleDay,
  getMenstrualCycleProps,
  getNumberOfDaysOfMenstruation,
  getOrganisation,
} from "../../helpers/userHelpers";
import { useExternalLink } from "../../hooks/useExternalLink";
import { onboardingDataSelector } from "../../slices/onboardingSlice";
import { userSelector } from "../../slices/userSlice";

interface MenstrualCycleDetailsScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

const HELP_ARTICLE_URL_FOR_MENSTRUAL_CYCLE_DETAILS_SCREEN =
  "https://killerbodyfood.com/blogs/voedingstips/tagged/menstruatiecyclus";

const MenstrualCycleDetailsScreen: React.FC<MenstrualCycleDetailsScreenProps> = ({ navigation }) => {
  const { colors } = useTheme();

  const { isLoading, error, openLink } = useExternalLink();

  const user = useSelector(userSelector);
  const onboardingData = useSelector(onboardingDataSelector);
  const { t, i18n } = useTranslation();

  const organisation = user ? getOrganisation(user) : undefined;
  const logoUri = organisation?.logo;

  // Get menstrual cycle props using the helper function
  const menstrualCycleProps = getMenstrualCycleProps(onboardingData);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.primary[10] }]}>
      <View style={styles.header}>
        <Icon name="arrow-back" size={24} onPress={() => navigation.goBack()} testID="back-button" />
      </View>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          {logoUri ? (
            <Image
              source={{ uri: logoUri }}
              style={{ width: Scale(160), height: VerticalScale(60) }}
              resizeMode="contain"
            />
          ) : (
            <Icon name="robot" type="material-community" size={64} color={colors.primary[600]} />
          )}

          <MenstrualCycleDiagram
            currentDayInCycle={menstrualCycleProps.currentDayInCycle}
            numberOfDaysInCycle={menstrualCycleProps.numberOfDaysInCycle}
            numberOfMenstruationDays={menstrualCycleProps.numberOfMenstruationDays}
            daysUntilNextPeriod={menstrualCycleProps.daysUntilNextPeriod}
          />

          <View style={styles.bottomSection}>
            <View style={styles.titleContainer}>
              <Text style={styles.headerText}>
                {t("onboarding.onboarding_menstrual_cycle_details_screen.personalizedCycleBasedDiets")}
              </Text>
              <Icon
                name="info-circle"
                type="font-awesome-5"
                size={24}
                color={HORMONE_HEX_COLOR}
                onPress={() => {
                  openLink(HELP_ARTICLE_URL_FOR_MENSTRUAL_CYCLE_DETAILS_SCREEN).catch((err) => {
                    console.error("Failed to open URL:", err);
                    Sentry.captureException(err);
                  });
                }}
                containerStyle={styles.infoIconContainer}
              />
            </View>
            <View style={styles.row}>
              <MaterialCommunityIcons
                name="seesaw"
                size={100}
                color={HORMONE_HEX_COLOR}
                style={styles.placeholderImage}
              />
              <View style={styles.column}>
                <Text style={[styles.phaseTitle, { backgroundColor: HORMONE_HEX_COLOR, textAlign: "center" }]}>
                  {t("onboarding.onboarding_menstrual_cycle_details_screen.lutealPhase")}
                </Text>
                <Text style={styles.phaseDescription}>
                  {t("onboarding.onboarding_menstrual_cycle_details_screen.lutealPhaseDescription")}
                </Text>
              </View>
            </View>
            <View style={styles.row}>
              <Ionicons name="flower" size={70} color={HORMONE_HEX_COLOR} style={styles.placeholderImage} />
              <View style={styles.column}>
                <Text style={[styles.phaseTitle, { backgroundColor: HORMONE_HEX_COLOR, textAlign: "center" }]}>
                  {t("onboarding.onboarding_menstrual_cycle_details_screen.follicularPhase")}
                </Text>
                <Text style={styles.phaseDescription}>
                  {t("onboarding.onboarding_menstrual_cycle_details_screen.follicularPhaseDescription")}
                </Text>
              </View>
            </View>
            <View style={[styles.nextButtonContainer, { backgroundColor: colors.primary[10] }]}>
              <Button
                style={[styles.nextButton, { backgroundColor: HORMONE_HEX_COLOR }]}
                onPress={() => {
                  navigation.navigate(Routes.MealTimesScreen);
                }}
                testID="menstrualCycleDetailsScreen-nextButton"
              >
                {t("onboarding.onboarding_menstrual_cycle_details_screen.nextButton")}
              </Button>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    alignItems: "center",
    padding: 16,
  },
  chartContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 40,
  },
  logo: {
    width: 160, // Adjust width as needed
    height: 60, // Adjust height as needed
    marginBottom: 20, // Space between logo and chart
  },
  textContainer: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    width: "80%",
  },
  daysUntilText: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
  },
  fertileText: {
    fontSize: 14,
    color: "#80a0ff",
    textAlign: "center",
  },
  headerText: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 16,
  },
  bottomSection: {
    marginTop: 20,
    paddingHorizontal: 16,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  column: {
    flex: 1,
    padding: 8,
  },
  placeholderImage: {
    width: 100,
    height: 100,
    marginBottom: 8,
  },
  phaseTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "left",
    padding: 8,
    marginBottom: 4,
    borderRadius: 8,
    color: "#fff",
  },
  phaseDescription: {
    fontSize: 14,
    textAlign: "left",
    color: "#333",
  },
  nextButtonContainer: {
    marginTop: 20,
    alignItems: "center",
    padding: 16,
    // backgroundColor set dynamically
  },
  nextButton: {
    marginTop: 20,
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    width: "100%",
  },
  titleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 16,
    marginBottom: 16,
  },
  infoIconContainer: {
    marginLeft: 8,
  },
});

export default MenstrualCycleDetailsScreen;
